import { useState } from "react";
import { repoErrorMessage } from "@/components/repo-lifecycle/lifecycleVocab";
import { toast } from "sonner";
import { FolderUp, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useBulkUpload } from "@/api/mutations/gitContent";
import { ApiError } from "@/api/client";

// #277 (UC-4): Ordner hochladen — viele Dateien, EIN Commit.
//
// Zwei Dinge, die die Maske leisten muss:
//  1. VORHER zeigen, was passieren wird (Dateiliste + Zielpfade). Ein Upload, der 30 Dateien
//     committet, ohne dass man sie gesehen hat, ist nicht überprüfbar.
//  2. Die Schema-Fundstellen des Servers ANZEIGEN, statt sie zu verschlucken — „ungültig"
//     allein hilft beim Reparieren nicht.

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  repoId: string;
  /** Aktueller Ordner im Browser — Vorschlag fürs Ziel. */
  currentPath: string;
  /** Entwurfs-Branch. Ohne ihn ist der Upload gesperrt (der Server lehnt Live ab). */
  ref: string | null;
  onDone?: () => void;
}

interface Auswahl {
  path: string;
  size: number;
  file: File;
}

const MAX_DATEIEN = 500;
const MAX_GESAMT = 50 * 1024 * 1024;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

async function alsBase64(f: File): Promise<string> {
  const buf = new Uint8Array(await f.arrayBuffer());
  let bin = "";
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

export function BulkUploadDialog({ open, onOpenChange, repoId, currentPath, ref: draftRef, onDone }: Props) {
  const [auswahl, setAuswahl] = useState<Auswahl[]>([]);
  const [ziel, setZiel] = useState(currentPath);
  const [message, setMessage] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [fundstellen, setFundstellen] = useState<Array<{ path: string; message: string }>>([]);
  const mutation = useBulkUpload();

  const gesamt = auswahl.reduce((n, a) => n + a.size, 0);
  const zuVieleDateien = auswahl.length > MAX_DATEIEN;
  const zuGross = gesamt > MAX_GESAMT;

  function close(o: boolean) {
    if (!o) {
      setAuswahl([]);
      setMessage("");
      setFehler(null);
      setFundstellen([]);
    }
    onOpenChange(o);
  }

  function waehlen(list: FileList | null) {
    setFehler(null);
    setFundstellen([]);
    const dateien: Auswahl[] = [];
    for (const f of Array.from(list ?? [])) {
      // `webkitRelativePath` trägt bei einer Ordner-Auswahl den Pfad INNERHALB des Ordners.
      // Das erste Segment ist der Ordnername selbst — den behalten wir bewusst: sonst
      // landet der Inhalt eines Plugins unbenannt im Zielordner.
      const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
      dateien.push({ path: rel, size: f.size, file: f });
    }
    setAuswahl(dateien);
  }

  async function submit() {
    setFehler(null);
    setFundstellen([]);
    if (!draftRef) {
      setFehler("Wähle zuerst einen Entwurf — auf den freigegebenen Stand kann nicht hochgeladen werden.");
      return;
    }
    try {
      const files = await Promise.all(
        auswahl.map(async (a) => ({ path: a.path, contentBase64: await alsBase64(a.file) })),
      );
      const res = await mutation.mutateAsync({
        repoId,
        targetPath: ziel.trim(),
        message: message.trim(),
        ref: draftRef,
        files,
      });
      toast.success(`${res.files.length} Dateien in einem Commit gespeichert`, {
        description: `Im Entwurf ${res.ref}`,
      });
      onDone?.();
      close(false);
    } catch (err) {
      // Die Fundstellen des Schema-Gates sind das Wertvollste an dieser Fehlermeldung.
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { issues?: Array<{ path: string; message: string }>; filePath?: string };
        setFundstellen(body?.issues ?? []);
        setFehler(`${body?.filePath ?? "Eine Datei"} ist nicht gültig:`);
        return;
      }
      // Alles außer dem 422-Sonderfall (Fundstellen oben) über die EINE Tabelle.
      setFehler(repoErrorMessage(err, "Hochladen fehlgeschlagen"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="dialog-bulk-upload">
        <DialogHeader>
          <DialogTitle>Ordner hochladen</DialogTitle>
          <DialogDescription>
            Alle Dateien werden als <strong>ein einziger Commit</strong> gespeichert — ein Plugin
            ist damit entweder ganz da oder gar nicht. Gleichnamige Dateien im Zielordner werden
            dabei <strong>überschrieben</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-files">Ordner auswählen</Label>
            <input
              id="bulk-files"
              type="file"
              multiple
              // @ts-expect-error — nicht im React-Typ, aber in allen Zielbrowsern vorhanden.
              webkitdirectory=""
              directory=""
              className="block w-full text-[13px] text-text2"
              onChange={(e) => waehlen(e.target.files)}
              data-testid="bulk-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bulk-target">Zielordner im Repository</Label>
            <Input
              id="bulk-target"
              value={ziel}
              onChange={(e) => setZiel(e.target.value)}
              placeholder="leer = Wurzel"
              data-testid="bulk-target"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bulk-msg">Beschreibung der Änderung</Label>
            <Input
              id="bulk-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="z.B. Plugin abaninja hinzugefügt"
            />
          </div>

          {auswahl.length > 0 && (
            <div className="rounded-lg border border-border bg-surface2 p-2" data-testid="bulk-preview">
              <p className="mb-1 text-[12.5px] text-text2">
                {auswahl.length} Dateien · {formatBytes(gesamt)} →{" "}
                <span className="mono">{ziel.trim() || "Wurzel"}</span>
              </p>
              <ul className="max-h-40 overflow-y-auto text-[12px] text-text3">
                {auswahl.slice(0, 100).map((a) => (
                  <li key={a.path} className="mono truncate">
                    {ziel.trim() ? `${ziel.trim()}/${a.path}` : a.path}
                  </li>
                ))}
              </ul>
              {auswahl.length > 100 && (
                <p className="mt-1 text-[12px] text-text3">… und {auswahl.length - 100} weitere</p>
              )}
            </div>
          )}

          {(zuVieleDateien || zuGross) && (
            <p className="text-[13px] text-danger">
              {zuVieleDateien ? `Höchstens ${MAX_DATEIEN} Dateien.` : "Die Auswahl ist größer als 50 MB."}
            </p>
          )}

          {!draftRef && (
            <p className="text-[13px] text-warn" data-testid="bulk-needs-draft">
              Es ist kein Entwurf ausgewählt. Der freigegebene Stand lässt sich nicht direkt
              beschreiben — lege einen Entwurf an oder wechsle zu einem.
            </p>
          )}

          {fehler && (
            <div className="text-[13px] text-danger" data-testid="bulk-error">
              <p>{fehler}</p>
              {fundstellen.length > 0 && (
                <ul className="mt-1 list-disc pl-4">
                  {fundstellen.map((i, n) => (
                    <li key={`${i.path}-${n}`}>
                      <span className="mono">{i.path || "(Datei)"}</span>: {i.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={mutation.isPending || auswahl.length === 0 || !draftRef || zuVieleDateien || zuGross}
            data-testid="bulk-submit"
          >
            {mutation.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <FolderUp className="mr-1 h-3.5 w-3.5" />
            )}
            {mutation.isPending ? "Lade hoch …" : `${auswahl.length} Dateien speichern`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
